/**
 * EOS reference material for the M&A GWC page — the three dimensions, the
 * People Analyzer scale, and the verdict matrix.
 *
 * Kept out of the component so the wording is reviewable in one place. Carried
 * across from the source app unchanged: this is EOS's language, not ours, and
 * paraphrasing it would quietly change what the manager is being asked.
 */
export interface DimensionMeta {
  key: 'GetIt' | 'WantIt' | 'Capacity';
  notesKey: 'GetItNotes' | 'WantItNotes' | 'CapacityNotes';
  letter: 'G' | 'W' | 'C';
  label: string;
  definition: string;
  prompts: string[];
}

export const GWC_DIMENSIONS: DimensionMeta[] = [
  {
    key: 'GetIt',
    notesKey: 'GetItNotes',
    letter: 'G',
    label: 'Get it',
    definition:
      'They genuinely grasp the seat — the role, the culture, the systems, the pace, and how their work fits the whole. Getting it is innate; it cannot be trained into someone.',
    prompts: [
      'Do they instinctively understand the role, without it being explained twice?',
      'Do they connect the dots between their work and the rest of the business?',
      'Do they get how we operate here — the pace, the standards, the way decisions get made?',
    ],
  },
  {
    key: 'WantIt',
    notesKey: 'WantItNotes',
    letter: 'W',
    label: 'Want it',
    definition:
      'They genuinely want this seat, on their own steam. No pep talk, incentive or pressure required to make them want it.',
    prompts: [
      'Do they want this seat with all its accountabilities — not just the title or the pay?',
      'Would they choose this role again today, knowing what it actually involves?',
      'Do they do the work without being pushed, or does the energy come from us?',
    ],
  },
  {
    key: 'Capacity',
    notesKey: 'CapacityNotes',
    letter: 'C',
    label: 'Capacity to do it',
    definition:
      'They have the capacity to do the job well — the time in their week, and the emotional, intellectual, physical and knowledge/skill capacity the seat demands.',
    prompts: [
      'Do they have the time to do this seat properly, alongside everything else on them?',
      'Do they have the knowledge and skill the seat requires today?',
      'Do they have the emotional and intellectual capacity for the hard parts of the role?',
    ],
  },
];

/** GWC is a Yes or a No per dimension — never a score. */
export const GWC_ANSWERS = [
  { value: 'yes', label: 'Yes', short: '✓' },
  { value: 'no', label: 'No', short: '✗' },
  { value: 'unknown', label: 'Not called', short: '?' },
] as const;

/** The People Analyzer scale for core values. */
export const VALUE_RATINGS = [
  { value: 'plus', label: 'Exhibits it most of the time', short: '+' },
  { value: 'plusminus', label: 'Exhibits it about half the time', short: '+/−' },
  { value: 'minus', label: 'Rarely exhibits it', short: '−' },
  { value: 'unrated', label: 'Not rated', short: '·' },
] as const;

export const GWC_ACTIONS = [
  { value: 'none', label: 'No decision yet' },
  { value: 'keep', label: 'Keep — right person, right seat' },
  { value: 'coach', label: 'Coach — 3-Strike Rule in progress' },
  { value: 'reseat', label: 'Move to a different seat' },
  { value: 'exit', label: 'Exit the business' },
] as const;

export type VerdictKey =
  | 'unassessed'
  | 'right_right'
  | 'right_person_wrong_seat'
  | 'wrong_person_right_seat'
  | 'wrong_wrong'
  | 'seat_yes'
  | 'seat_no';

export interface VerdictMeta {
  label: string;
  /** Maps to a badge class. The brand has no green, so "good" is navy. */
  badge: string;
  advice: string;
}

export const VERDICTS: Record<VerdictKey, VerdictMeta> = {
  unassessed: {
    label: 'Not assessed',
    badge: 'badge-neutral',
    advice:
      'Answer all three GWC questions and rate every core value to get a verdict. Only the person’s direct manager should make the call.',
  },
  right_right: {
    label: 'Right Person, Right Seat',
    badge: 'badge-good',
    advice:
      'Keep. Make sure they know what is expected of the seat, and give them the tools and the time to do it.',
  },
  right_person_wrong_seat: {
    label: 'Right Person, Wrong Seat',
    badge: 'badge-warning',
    advice:
      'They share the values, so keep them in the business — but not here. If the gap is fixable, run the 3-Strike Rule; if not, find the seat they do GWC.',
  },
  wrong_person_right_seat: {
    label: 'Wrong Person, Right Seat',
    badge: 'badge-warning',
    advice:
      'Capable in the seat but off on the values. Have the values conversation directly and set a deadline — values gaps rarely close on their own.',
  },
  wrong_wrong: {
    label: 'Wrong Person, Wrong Seat',
    badge: 'badge-critical',
    advice: 'Neither the values nor the seat fit. Move them out of the business.',
  },
  seat_yes: {
    label: 'Right Seat — GWC Yes',
    badge: 'badge-good',
    advice:
      'A Yes on all three, so the seat fits. Add your core values on the V/TO to also answer whether they are the right person.',
  },
  seat_no: {
    label: 'Wrong Seat — GWC No',
    badge: 'badge-critical',
    advice:
      'A No on any one of G, W or C is a No on the seat. Add your core values on the V/TO to see whether they belong in a different seat or out of the business.',
  },
};

export interface GwcPerson {
  MaGwcPersonId: string;
  Name: string;
  Seat: string;
  Department: string;
  Manager: string;
  AssessedBy: string;
  AssessedOn: string;
  GetIt: string;
  GetItNotes: string;
  WantIt: string;
  WantItNotes: string;
  Capacity: string;
  CapacityNotes: string;
  Action: string;
  ActionNotes: string;
  SortOrder: number;
}
/** One of the company's core values, from the V/TO. */
export interface CoreValue {
  CoreValueId: number;
  Name: string;
  Description: string | null;
  SortOrder: number;
}
export interface Rating {
  MaGwcPersonId: string;
  CoreValueId: number;
  Rating: string;
}

/**
 * The verdict, derived rather than stored.
 *
 * RIGHT SEAT is all three of G, W and C answered Yes — two out of three is a
 * No, which is why this is not a score.
 *
 * RIGHT PERSON is the People Analyzer bar: a "+" on the majority of the core
 * values, and no "−" on any of them. One minus fails it regardless of the rest.
 *
 * With no core values defined, only the seat half can be answered — hence the
 * two seat-only verdicts.
 */
export function verdictFor(
  person: GwcPerson,
  values: CoreValue[],
  ratings: Rating[],
): VerdictKey {
  const answered =
    person.GetIt !== 'unknown' && person.WantIt !== 'unknown' && person.Capacity !== 'unknown';
  if (!answered) return 'unassessed';

  const rightSeat = person.GetIt === 'yes' && person.WantIt === 'yes' && person.Capacity === 'yes';

  if (values.length === 0) return rightSeat ? 'seat_yes' : 'seat_no';

  const mine = values.map(
    (v) =>
      ratings.find(
        (r) => r.MaGwcPersonId === person.MaGwcPersonId && r.CoreValueId === v.CoreValueId,
      )?.Rating ?? 'unrated',
  );
  if (mine.some((r) => r === 'unrated')) return 'unassessed';

  const plus = mine.filter((r) => r === 'plus').length;
  const anyMinus = mine.some((r) => r === 'minus');
  const rightPerson = !anyMinus && plus > values.length / 2;

  if (rightPerson && rightSeat) return 'right_right';
  if (rightPerson && !rightSeat) return 'right_person_wrong_seat';
  if (!rightPerson && rightSeat) return 'wrong_person_right_seat';
  return 'wrong_wrong';
}
